import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Gift, Star, Crown, Award, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  {
    name: 'Bronze',
    icon: Award,
    threshold: 0,
    benefits: [
      'Welcome bonus: 100 points',
      '1 point per ₹100 spent',
      'Birthday rewards',
      'Early access to sales'
    ]
  },
  {
    name: 'Silver',
    icon: Star,
    threshold: 2000,
    benefits: [
      'All Bronze benefits',
      '1.5x points on purchases',
      'Free gift wrapping',
      'Exclusive member-only products',
      'Priority customer service'
    ]
  },
  {
    name: 'Gold',
    icon: Crown,
    threshold: 5000,
    benefits: [
      'All Silver benefits',
      '2x points on purchases',
      'Free express shipping',
      'Exclusive previews',
      'Personal styling consultation',
      'VIP event invitations'
    ]
  }
];

const LoyaltyPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (token) {
      fetchLoyaltyData();
    }
  }, [token]);

  const fetchLoyaltyData = async () => {
    try {
      const [balanceRes, transRes] = await Promise.all([
        axios.get(`${API}/loyalty/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loyalty/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLoyaltyData(balanceRes.data);
      setTransactions(transRes.data.transactions || []);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    }
  };

  return (
    <div className="pt-24 pb-24" data-testid="loyalty-page">
      {/* Hero Banner */}
      <section className="relative h-[45vh] min-h-[350px] mb-24">
        <img 
          src="/images/banners/club-banner.png" 
          alt="Chaleur Club" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="chaleur-container">
            <div className="max-w-2xl text-white">
              <p className="caption text-white/80 mb-4">Chaleur Club</p>
              <h1 className="text-4xl md:text-5xl font-light mb-6">
                Rewards for Those Who <span className="editorial-italic">Give</span>
              </h1>
              <p className="text-white/80 leading-relaxed mb-8">
                Join the Chaleur Club and be rewarded for embracing the art of giving. 
                Earn points on every purchase, unlock exclusive benefits, and be part of 
                a community that values warmth and generosity.
              </p>
              {!isAuthenticated && (
                <Button asChild className="bg-white text-foreground hover:bg-white/90">
                  <Link to="/register">Join Now - Get 100 Points Free</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Member Status Card - Show only if logged in */}
      {isAuthenticated && loyaltyData && (
        <section className="chaleur-container mb-24">
          <div className="max-w-md mx-auto p-8 bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                {loyaltyData.tier === 'Gold' ? <Crown className="w-8 h-8 text-primary" /> :
                 loyaltyData.tier === 'Silver' ? <Star className="w-8 h-8 text-primary" /> :
                 <Award className="w-8 h-8 text-primary" />}
              </div>
              <div>
                <p className="caption text-primary">{loyaltyData.tier} Member</p>
                <p className="text-sm text-muted-foreground">{user?.first_name}</p>
              </div>
            </div>
            <div className="text-4xl font-light mb-2">{loyaltyData.points}</div>
            <p className="text-muted-foreground mb-6">Available Points</p>
            {loyaltyData.next_tier_at && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span>{loyaltyData.tier}</span>
                  <span>{loyaltyData.next_tier_at - loyaltyData.points} to next tier</span>
                </div>
                <div className="h-2 bg-white/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(loyaltyData.points / loyaltyData.next_tier_at) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Tiers */}
      <section className="section-spacing bg-secondary/30">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">Membership Tiers</p>
            <h2 className="text-3xl md:text-4xl font-light">Unlock More as You Give</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {TIERS.map((tier, idx) => {
              const Icon = tier.icon;
              const isActive = loyaltyData?.tier === tier.name;
              return (
                <div 
                  key={tier.name}
                  className={`p-8 border transition-colors ${
                    isActive ? 'border-primary bg-primary/5' : 'border-border bg-background'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <Icon className={`w-8 h-8 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div>
                      <h3 className="text-xl">{tier.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {tier.threshold === 0 ? 'Starting tier' : `${tier.threshold}+ points`}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {tier.benefits.map((benefit, bidx) => (
                      <li key={bidx} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Points Calculator */}
      <section className="section-spacing bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="chaleur-container">
          <div className="text-center mb-12">
            <p className="caption text-amber-700 mb-4">See Your Rewards</p>
            <h2 className="text-3xl md:text-4xl font-light">How Rewarding is Your Next Purchase?</h2>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-sm text-muted-foreground mb-2">Buy a ₹15,000 Shawl</p>
                <p className="text-3xl font-light text-amber-600 mb-2">150</p>
                <p className="text-sm">Points earned (Bronze)</p>
                <p className="text-xs text-amber-600 mt-2">= ₹150 rewards</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-amber-300">
                <p className="text-sm text-muted-foreground mb-2">Buy a ₹45,000 Pashmina</p>
                <p className="text-3xl font-light text-amber-600 mb-2">675</p>
                <p className="text-sm">Points earned (Silver 1.5x)</p>
                <p className="text-xs text-amber-600 mt-2">= ₹675 rewards</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-sm text-muted-foreground mb-2">Buy a ₹75,000 Heritage</p>
                <p className="text-3xl font-light text-amber-600 mb-2">1,500</p>
                <p className="text-sm">Points earned (Gold 2x)</p>
                <p className="text-xs text-amber-600 mt-2">= ₹1,500 rewards</p>
              </div>
            </div>
            <p className="text-center text-sm text-amber-700 mt-6">
              Higher tiers = More points per purchase. It pays to be loyal!
            </p>
          </div>
        </div>
      </section>

      {/* How to Earn */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">How to Earn</p>
            <h2 className="text-3xl md:text-4xl font-light">Multiple Ways to Earn Points</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { title: 'Shop', points: '1-2×', desc: 'Earn points on every purchase based on your tier' },
              { title: 'Refer', points: '500', desc: 'Get 500 points when a friend makes their first purchase' },
              { title: 'Review', points: '50', desc: 'Share your thoughts and earn 50 points per review' },
              { title: 'Birthday', points: '200', desc: 'Receive 200 bonus points on your birthday' }
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 border border-border">
                <div className="text-3xl font-light text-primary mb-2">{item.points}</div>
                <h3 className="font-medium mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Redemption */}
      <section className="section-spacing bg-foreground text-primary-foreground">
        <div className="chaleur-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="caption text-white/60 mb-4">Redeem Your Points</p>
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              Turn Points into Rewards
            </h2>
            <p className="text-white/70 mb-8">
              Redeem your points for discounts on your next purchase. 
              Every 100 points = ₹100 off. Simple as that.
            </p>
            <div className="grid grid-cols-3 gap-8">
              <div>
                <div className="text-3xl font-light">100</div>
                <p className="text-sm text-white/60">= ₹100 off</p>
              </div>
              <div>
                <div className="text-3xl font-light">500</div>
                <p className="text-sm text-white/60">= ₹500 off</p>
              </div>
              <div>
                <div className="text-3xl font-light">1000</div>
                <p className="text-sm text-white/60">= ₹1000 off</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transaction History (for logged in users) */}
      {isAuthenticated && transactions.length > 0 && (
        <section className="section-spacing">
          <div className="chaleur-container">
            <h2 className="text-2xl font-light mb-8">Recent Activity</h2>
            <div className="space-y-4">
              {transactions.slice(0, 10).map(tx => (
                <div key={tx.id} className="flex justify-between items-center p-4 border border-border">
                  <div>
                    <p className="font-medium">{tx.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`text-lg font-medium ${tx.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.points > 0 ? '+' : ''}{tx.points}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {!isAuthenticated && (
        <section className="section-spacing">
          <div className="chaleur-container text-center">
            <Gift className="w-16 h-16 mx-auto mb-6 text-primary" />
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              Join Chaleur Club Today
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              Create an account and instantly receive 100 welcome points. 
              Start earning rewards on every purchase.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="btn-chaleur-primary">
                <Link to="/register">Join Now</Link>
              </Button>
              <Button asChild variant="outline" className="btn-chaleur-secondary">
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LoyaltyPage;
